import type { ReactNode, ButtonHTMLAttributes } from 'react'

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-xl font-bold text-text-light dark:text-text-dark tracking-display">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1 text-sm text-subtle-light dark:text-subtle-dark">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  )
}

export function Card({
  children,
  className = '',
  title,
}: {
  children: ReactNode
  className?: string
  title?: string
}) {
  return (
    <div
      className={`bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark ${className}`}
    >
      {title && (
        <div className="px-5 py-3 border-b border-border-light dark:border-border-dark">
          <h2 className="text-sm font-semibold text-text-light dark:text-text-dark">{title}</h2>
        </div>
      )}
      <div className={title ? 'p-5' : 'p-5'}>{children}</div>
    </div>
  )
}

export function Stat({
  label,
  value,
  hint,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
}) {
  return (
    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-5">
      <p className="label-sm text-subtle-light dark:text-subtle-dark">{label}</p>
      <p className="mt-1 text-2xl font-bold text-text-light dark:text-text-dark">{value}</p>
      {hint && <p className="mt-1 text-xs text-subtle-light dark:text-subtle-dark">{hint}</p>}
    </div>
  )
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  icon?: string
}

export function Button({
  variant = 'secondary',
  icon,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  const base =
    'inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
  const variants: Record<string, string> = {
    primary: 'bg-primary text-white hover:bg-primary/90',
    secondary:
      'bg-container-light dark:bg-container-dark text-text-light dark:text-text-dark hover:bg-primary/5',
    ghost:
      'text-subtle-light dark:text-subtle-dark hover:bg-primary/5 hover:text-text-light dark:hover:text-text-dark',
    danger: 'bg-error/10 text-error hover:bg-error/20',
  }
  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...rest}>
      {icon && <span className="material-icons text-base">{icon}</span>}
      {children}
    </button>
  )
}

const fieldCls =
  'w-full px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50'

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
        {label}
      </span>
      {children}
    </label>
  )
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${fieldCls} ${props.className ?? ''}`} />
}

export function SelectInput(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${fieldCls} ${props.className ?? ''}`} />
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${fieldCls} font-mono ${props.className ?? ''}`} />
}
